import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMobile } from '../hooks/useMobile';

/**
 * ResponsiveHeader — Page header that collapses secondary buttons into a menu on mobile.
 * 
 * Props:
 * - title: string | ReactNode — page title
 * - subtitle: string | ReactNode — optional subtitle
 * - onBack: () => void — show back arrow (mobile detail pages)
 * - primaryAction: { label, icon, onClick, color, variant, disabled } — main visible button
 * - secondaryActions: [{ label, icon, onClick, color, disabled, divider }] — collapse into menu on mobile
 * - children: ReactNode — extra content (chips, badges, etc.)
 */
const ResponsiveHeader = ({
  title,
  subtitle,
  onBack,
  primaryAction,
  secondaryActions = [],
  children,
}) => {
  const { isMobile } = useMobile();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const visibleSecondaryActions = secondaryActions.filter(a => !a.hidden);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 1 : 2,
        mb: 2,
      }}
    >
      {/* Title section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {onBack && (
          <IconButton onClick={onBack} size="small" sx={{ mr: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        {children && !isMobile && (
          <Box sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            {children}
          </Box>
        )}
      </Box>

      {/* Children on mobile - below title */}
      {children && isMobile && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {children}
        </Box>
      )}

      {/* Actions section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, justifyContent: isMobile ? 'flex-end' : 'flex-end' }}>
        {/* On desktop: show all buttons */}
        {!isMobile && visibleSecondaryActions.map((action, idx) => (
          <Button
            key={idx}
            variant={action.variant || 'outlined'}
            color={action.color || 'primary'}
            size="small"
            startIcon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        ))}

        {/* Primary action - always visible */}
        {primaryAction && (
          <Button
            variant={primaryAction.variant || 'contained'}
            color={primaryAction.color || 'primary'}
            size={isMobile ? 'medium' : 'medium'}
            startIcon={primaryAction.icon}
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            sx={isMobile ? { flex: 1 } : {}}
          >
            {primaryAction.label}
          </Button>
        )}

        {/* On mobile: secondary actions in MoreVert menu */}
        {isMobile && visibleSecondaryActions.length > 0 && (
          <>
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              PaperProps={{ sx: { minWidth: 200 } }}
            >
              {visibleSecondaryActions.map((action, idx) => (
                <React.Fragment key={idx}>
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      action.onClick && action.onClick();
                    }}
                    disabled={action.disabled}
                    sx={action.color === 'error' ? { color: 'error.main' } : {}}
                  >
                    {action.icon && (
                      <ListItemIcon sx={action.color === 'error' ? { color: 'error.main' } : {}}>
                        {action.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText>{action.label}</ListItemText>
                  </MenuItem>
                </React.Fragment>
              ))}
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ResponsiveHeader;
